import React from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import ArrowLeft from "../../svg/ArrowLeft";
import ArrowRight from "../../svg/ArrowRight";
import RoundedIconContainer from "./RoundedIconContainer";

type Props = {
  /** The period's text, e.g. "Friday, Oct 09" or "Oct 5 – Oct 11, 2026". */
  label: string;
  /** What one step covers; names the link, e.g. "Back to current week". */
  period: "day" | "week" | "month" | "year";
  /** Hides the "Back to current …" link while the period holds today. */
  containsToday: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onToday: () => void;
};

/**
 * The ‹ label › navigator shared by the History › Timeline toolbar and the
 * nutrition plan header: arrows either side of a single-line period label,
 * with the "Back to current {period}" link above it when the period is not the
 * current one. The label never wraps; the fixed minimum width keeps the
 * arrows from shifting as the label's length changes.
 */
const PeriodNavigator = ({
  label,
  period,
  containsToday,
  onPrevious,
  onNext,
  onToday,
}: Props) => {
  const theme: Theme = useTheme();

  return (
    <div style={styles.container}>
      <RoundedIconContainer handleClick={onPrevious}>
        <ArrowLeft size={16} fill={theme.colors_ds.NEUTRAL_ICON_STRONG} />
      </RoundedIconContainer>
      <div style={styles.text}>
        <div
          style={{
            ...styles.todayLink(theme),
            visibility: containsToday ? "hidden" : "visible",
          }}
          onClick={onToday}
        >
          Back to current {period}
        </div>
        <div style={styles.label(theme)}>{label}</div>
      </div>
      <RoundedIconContainer handleClick={onNext}>
        <ArrowRight size={16} fill={theme.colors_ds.NEUTRAL_ICON_STRONG} />
      </RoundedIconContainer>
    </div>
  );
};

export default PeriodNavigator;

const styles = {
  container: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  text: {
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    minWidth: "150px",
  },
  todayLink: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
    lineHeight: "16px",
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    cursor: "pointer",
    userSelect: "none" as "none",
  }),
  label: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "13px",
    lineHeight: "18px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    whiteSpace: "nowrap" as "nowrap",
    userSelect: "none" as "none",
  }),
};
